import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, HeartHandshakeIcon, MicroscopeIcon, CompassIcon, SparklesIcon, CheckCircle2Icon } from 'lucide-react';
const BLUE = '#2563EB';
const TEAL = '#0F9E8E';
const REDI_IMG = 'https://cdn.magicpatterns.com/uploads/cuSDustQLFMoA2E1aRSeBS/file_00000000117c71f5a64341e87ebc6818.png';
const values = [{
  icon: MicroscopeIcon,
  title: 'Evidence first',
  text: 'Everything we build is grounded in current nutrition support science and reviewed by Registered Dietitians.'
}, {
  icon: CompassIcon,
  title: 'Focused by design',
  text: 'We cut the noise so your study time goes toward what actually shows up on the exam.'
}, {
  icon: HeartHandshakeIcon,
  title: 'Genuinely supportive',
  text: 'Preparing for certification is hard. Our tools are made to keep you steady and motivated.'
}, {
  icon: SparklesIcon,
  title: 'Always improving',
  text: 'We continuously refine our questions, flashcards, and study flow based on how learners really study.'
}];
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 22
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
export function RDReadyAbout() {
  return <div className="w-full overflow-hidden bg-white text-[#1E293B]" style={{
    fontFamily: '"Poppins", "Inter", system-ui, sans-serif'
  }} data-id="element-2415">
      {/* HERO */}
      <section className="relative px-5 pt-14 pb-10 md:px-8 md:pt-20 md:pb-16" data-id="element-2416">
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.05fr_.95fr]" data-id="element-2417">
          <motion.div initial="hidden" animate="visible" variants={{
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }} data-id="element-2418">
            <motion.p variants={fadeUp} transition={{
            duration: 0.5
          }} className="text-xs font-bold uppercase tracking-[0.22em]" style={{
            color: TEAL
          }} data-id="element-2419">
              About Us
            </motion.p>
            <motion.h1 variants={fadeUp} transition={{
            duration: 0.5
          }} className="mt-4 text-4xl font-extrabold leading-[1.08] tracking-tight text-[#0F172A] sm:text-5xl lg:text-6xl" data-id="element-2420">
              Study tools built by{' '}
              <span style={{
              color: BLUE
            }} data-id="element-2421">
                dietitians,
              </span>{' '}
              for{' '}
              <span style={{
              color: TEAL
            }} data-id="element-2422">
                dietitians.
              </span>
            </motion.h1>
            <motion.p variants={fadeUp} transition={{
            duration: 0.5
          }} className="mt-6 max-w-lg text-base leading-relaxed text-[#5A6B7B]" data-id="element-2423">
              RD Ready started with a simple belief: preparing for the RD and
              CNSC exams shouldn&apos;t feel scattered or overwhelming. We
              create clear, evidence-based study tools that help nutrition
              professionals walk into their exam with confidence.
            </motion.p>
            <motion.div variants={fadeUp} transition={{
            duration: 0.5
          }} className="mt-8" data-id="element-2424">
              <Link to="/cnsc-prep" className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-0.5" style={{
              backgroundColor: BLUE
            }} data-id="element-2425">
                Explore our study tools <ArrowRightIcon className="h-4 w-4" data-id="element-2426" />
              </Link>
            </motion.div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.15
        }} className="relative flex items-center justify-center" data-id="element-2427">
            <div className="relative flex aspect-square w-full max-w-md items-center justify-center" data-id="element-2428">
              <div className="absolute inset-6 rounded-full bg-[#EEF4FF]" data-id="element-2429" />
              <img src={REDI_IMG} alt="Redi, the RD Ready study guide, waving" className="relative z-10 w-[86%] object-contain drop-shadow-sm" data-id="element-2430" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* MISSION */}
      <section className="px-5 py-12 md:px-8 md:py-16" data-id="element-2431">
        <div className="mx-auto max-w-4xl rounded-3xl bg-[#F7F9FC] px-6 py-12 text-center md:px-12 md:py-16" data-id="element-2432">
          <h2 className="text-2xl font-bold text-[#0F172A] md:text-3xl" data-id="element-2433">
            Our mission
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-[#5A6B7B] md:text-lg" data-id="element-2434">
            To make high-quality exam preparation accessible, focused, and
            genuinely helpful — so every nutrition professional has a clear path
            to certification and the confidence that comes with it.
          </p>
        </div>
      </section>

      {/* VALUES */}
      <section className="px-5 py-8 md:px-8 md:py-12" data-id="element-2435">
        <div className="mx-auto max-w-7xl" data-id="element-2436">
          <h2 className="text-center text-2xl font-bold text-[#0F172A] md:text-3xl" data-id="element-2437">
            What we stand for
          </h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-2438">
            {values.map(({
            icon: Icon,
            title,
            text
          }, i) => <motion.article key={title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.08
          }} className="rounded-2xl bg-white p-6 text-center shadow-[0_2px_16px_rgba(15,23,42,.05)]" data-id="element-2439">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full" style={{
              backgroundColor: '#EEF4FF'
            }} data-id="element-2440">
                  <Icon className="h-6 w-6" style={{
                color: BLUE
              }} data-id="element-2441" />
                </span>
                <h3 className="mt-4 text-base font-bold text-[#0F172A]" data-id="element-2442">
                  {title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-[#5A6B7B]" data-id="element-2443">
                  {text}
                </p>
              </motion.article>)}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="px-5 py-12 md:px-8 md:py-16" data-id="element-2444">
        <div className="mx-auto grid max-w-7xl items-center gap-10 rounded-3xl bg-[#F0FBF9] px-6 py-12 md:grid-cols-2 md:px-12 md:py-14" data-id="element-2445">
          <div data-id="element-2446">
            <p className="text-xs font-bold uppercase tracking-[0.22em]" style={{
            color: TEAL
          }} data-id="element-2447">
              Who it&apos;s for
            </p>
            <h2 className="mt-4 text-2xl font-bold text-[#0F172A] md:text-3xl" data-id="element-2448">
              Made for every stage of your journey.
            </h2>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-[#5A6B7B]" data-id="element-2449">
              Whether you&apos;re sitting for the RD exam for the first time or
              advancing into nutrition support with the CNSC, RD Ready meets you
              where you are.
            </p>
          </div>
          <ul className="grid gap-3" data-id="element-2450">
            {['Students preparing for the RD exam', 'Clinicians pursuing CNSC certification', 'Professionals returning to study after time away', 'Anyone who wants a calmer, more structured review'].map(item => <li key={item} className="flex items-center gap-3 rounded-2xl bg-white p-4 text-sm font-medium text-[#334155] shadow-[0_2px_12px_rgba(15,23,42,.04)]" data-id="element-2451">
                <CheckCircle2Icon className="h-5 w-5 shrink-0" style={{
              color: TEAL
            }} data-id="element-2452" />
                {item}
              </li>)}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 pb-16 pt-6 md:px-8 md:pb-24" data-id="element-2453">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 rounded-2xl bg-[#F7F9FC] px-6 py-8 text-center md:flex-row md:text-left md:px-10" data-id="element-2454">
          <div data-id="element-2455">
            <p className="text-lg font-bold text-[#0F172A]" data-id="element-2456">
              Have a question before you start?
            </p>
            <p className="text-sm text-[#5A6B7B]" data-id="element-2457">
              We&apos;d love to hear from you.
            </p>
          </div>
          <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5" style={{
          backgroundColor: BLUE
        }} data-id="element-2458">
            Contact us <ArrowRightIcon className="h-4 w-4" data-id="element-2459" />
          </Link>
        </div>

        <p className="mx-auto mt-8 max-w-3xl text-center text-xs leading-relaxed text-[#94A3B8]" data-id="element-2460">
          RD Ready is an independent study resource and is not affiliated with
          or endorsed by any certification board. Use of RD Ready does not
          guarantee examination outcomes.
        </p>
      </section>
    </div>;
}